import React, { useCallback, useEffect, useRef, useState } from "react";
import { createGame, moveTank, setPower, fire, tickProjectile } from "./engine.js";
import type { TankWarsGame } from "./types.js";
import { CANVAS_W, CANVAS_H, GROUND_Y, MAX_POWER } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "cpu" | "local" | "multiplayer";
type PlayerColor = "red" | "yellow" | "green" | "purple" | "orange" | "teal";

const COLOR_OPTIONS: PlayerColor[] = ["red", "yellow", "green", "purple", "orange", "teal"];

const CODE_WORDS = [
  "apple", "beach", "candy", "dance", "eagle", "flame", "grape", "honey",
  "ivory", "jelly", "koala", "lemon", "mango", "noble", "ocean", "pearl",
  "queen", "rain", "sugar", "tiger", "umbra", "vivid", "whale", "xenon",
  "yacht", "zebra", "bloom", "coral", "dawn", "ember", "frost", "glow",
  "haven", "iris", "jade", "kite", "lune", "mist", "nova", "opal",
];

function generateCode(): string {
  const pick = () => CODE_WORDS[Math.floor(Math.random() * CODE_WORDS.length)];
  return `${pick()}-${pick()}-${pick()}`;
}

export default function TankWarsPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("cpu");
  const [game, setGame] = useState<TankWarsGame>(() => createGame());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef(game);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(0);
  const mouseDownRef = useRef(false);
  const chargeRef = useRef(0);
  const [animShot, setAnimShot] = useState<{ fromX: number; fromY: number; angle: number; power: number } | null>(null);
  const animStartRef = useRef(0);
  const prevGameRef = useRef(game);

  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<1 | 2 | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");
  const [joinCode, setJoinCode] = useState(generateCode);
  const [p1Name, setP1Name] = useState("Player 1");
  const [p1Color, setP1Color] = useState<PlayerColor>("red");

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  const mpConnectedRef = useRef(mpConnected);

  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;
  mpConnectedRef.current = mpConnected;
  gameRef.current = game;

  function resetGame() {
    if (mode === "multiplayer") {
      apiPost("/api/tankwars/reset", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    setGame(createGame());
    chargeRef.current = 0;
  }

  function drawTank(ctx: CanvasRenderingContext2D, t: TankWarsGame["tanks"][0], player: 0 | 1) {
    const color = player === 0 ? "#3498db" : "#e74c3c";
    const darkColor = player === 0 ? "#1a5276" : "#922b21";
    const trackColor = player === 0 ? "#1a3a5c" : "#5c1a1a";
    const cx = t.x;
    const cy = t.y + t.height;
    ctx.fillStyle = trackColor;
    ctx.fillRect(cx - t.width / 2 - 2, cy - 4, t.width + 4, 8);
    ctx.fillStyle = darkColor;
    ctx.beginPath();
    ctx.roundRect(cx - t.width / 2 + 2, cy - t.height, t.width - 4, t.height - 2, 3);
    ctx.fill();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(cx - t.width / 2 + 4, cy - t.height + 2, t.width - 8, t.height - 6, 2);
    ctx.fill();
    const dir = player === 0 ? 1 : -1;
    const angleRad = (t.angle * Math.PI) / 180;
    const turretCX = cx;
    const turretCY = cy - t.height + 2;
    ctx.fillStyle = darkColor;
    ctx.beginPath();
    ctx.arc(turretCX, turretCY, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(turretCX, turretCY);
    ctx.lineTo(turretCX + Math.cos(angleRad) * dir * 22, turretCY - Math.sin(angleRad) * 22);
    ctx.stroke();
    ctx.strokeStyle = darkColor;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(turretCX, turretCY);
    ctx.lineTo(turretCX + Math.cos(angleRad) * dir * 22, turretCY - Math.sin(angleRad) * 22);
    ctx.stroke();
    ctx.fillStyle = "#fff";
    ctx.font = "bold 11px monospace";
    ctx.textAlign = "center";
    ctx.fillText(`HP ${t.health}`, cx, cy - t.height - 10);
  }

  // Cosmetic projectile animation
  function renderAnimProjectile(ctx: CanvasRenderingContext2D, now: number) {
    if (!animShot) return;
    const elapsed = now - animStartRef.current;
    const duration = 1200;
    if (elapsed > duration) { setAnimShot(null); return; }
    const t = elapsed / duration;
    const g = gameRef.current;
    const dir = g.turn === 0 ? 1 : -1;
    const speed = animShot.power * 4 + 80;
    const angleRad = (animShot.angle * Math.PI) / 180;
    const x = animShot.fromX + Math.cos(angleRad) * dir * speed * t * 0.7;
    const y = animShot.fromY - Math.sin(angleRad) * speed * t * 0.7 + 0.5 * 280 * (t * 0.7) * (t * 0.7);
    ctx.fillStyle = "#fff";
    ctx.shadowBlur = 40;
    ctx.shadowColor = "#ff8800";
    ctx.beginPath();
    ctx.arc(x, y, 8 + (1 - t) * 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ffd700";
    ctx.shadowBlur = 30;
    ctx.shadowColor = "#ff4400";
    ctx.beginPath();
    ctx.arc(x, y, 5 + (1 - t) * 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  function render(ctx: CanvasRenderingContext2D, g: TankWarsGame) {
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    const grad = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
    grad.addColorStop(0, "#0a1628");
    grad.addColorStop(0.7, "#152238");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.fillStyle = "#3a5a2a";
    ctx.fillRect(0, GROUND_Y, CANVAS_W, CANVAS_H - GROUND_Y);
    ctx.fillStyle = "#4a7a3a";
    ctx.fillRect(0, GROUND_Y, CANVAS_W, 3);
    ctx.fillStyle = "#2a4a1a";
    for (let x = 0; x < CANVAS_W; x += 20) {
      const h = 3 + Math.sin(x * 0.3) * 2;
      ctx.fillRect(x, GROUND_Y - h, 2, h);
    }

    if (g.projectile) {
      for (const t of g.projectile.trail) {
        const i = g.projectile.trail.indexOf(t);
        const alpha = 0.8 - (i / g.projectile.trail.length) * 0.6;
        const size = 3 + (i / g.projectile.trail.length) * 4;
        ctx.fillStyle = `rgba(255, 180, 50, ${alpha})`;
        ctx.beginPath();
        ctx.arc(t.x, t.y, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = "#fff";
      ctx.shadowBlur = 50;
      ctx.shadowColor = "#ff8800";
      ctx.beginPath();
      ctx.arc(g.projectile.x, g.projectile.y, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffd700";
      ctx.shadowBlur = 40;
      ctx.shadowColor = "#ff4400";
      ctx.beginPath();
      ctx.arc(g.projectile.x, g.projectile.y, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    for (const e of g.explosions) {
      const et = e.timer / e.maxTimer;
      const r = 18 + (1 - et) * 30;
      const alpha = et;
      ctx.shadowBlur = 40;
      ctx.shadowColor = `rgba(255, 150, 30, ${alpha})`;
      ctx.fillStyle = `rgba(255, 200, 50, ${alpha * 0.9})`;
      ctx.beginPath();
      ctx.arc(e.x, e.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255, 80, 20, ${alpha * 0.8})`;
      ctx.beginPath();
      ctx.arc(e.x, e.y, r * 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255, 255, 200, ${alpha * 0.5})`;
      ctx.beginPath();
      ctx.arc(e.x, e.y, r * 0.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    for (let i = 0; i < 2; i++) {
      if (g.tanks[i].health > 0) drawTank(ctx, g.tanks[i], i as 0 | 1);
    }

    if (mouseDownRef.current && !g.inFlight && !g.winner) {
      const charge = chargeRef.current / 100;
      const barX = CANVAS_W / 2 - 80;
      const barY = CANVAS_H - 30;
      ctx.fillStyle = "rgba(0,0,0,0.5)";
      ctx.roundRect(barX, barY, 160, 14, 7);
      ctx.fill();
      ctx.fillStyle = charge < 0.5 ? "#00ff88" : charge < 0.85 ? "#ffcc00" : "#ff4444";
      ctx.roundRect(barX + 2, barY + 2, Math.max(4, (160 - 4) * charge), 10, 5);
      ctx.fill();
    }

    renderAnimProjectile(ctx, performance.now());

    ctx.fillStyle = "#fff";
    ctx.font = "14px monospace";
    ctx.textAlign = "left";
    ctx.fillText(`Wind: ${g.wind.toFixed(1)}`, CANVAS_W - 140, 24);
    ctx.fillText(`Angle: ${g.tanks[g.turn].angle.toFixed(0)}°`, 10, 24);
    ctx.fillText(`Power: ${g.tanks[g.turn].power}`, 10, 44);
  }

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;
    render(ctx, game);
  }, [game, animShot]);

  // Game loop
  useEffect(() => {
    lastTimeRef.current = performance.now();
    function loop(time: number) {
      const dt = Math.min(time - lastTimeRef.current, 50);
      lastTimeRef.current = time;
      setGame((prev) => {
        let g = prev;
        if (!g.inFlight && !g.winner) {
          if (mouseDownRef.current) {
            chargeRef.current = Math.min(MAX_POWER, chargeRef.current + dt * 0.01);
            g = setPower(g, chargeRef.current);
          }
        }
        if (g.inFlight) {
          g = tickProjectile(g, dt);
        }
        return g;
      });
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Mouse aim + fire
  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const g = gameRef.current;
    if (g.inFlight || g.winner) return;
    if (mode === "multiplayer" && !yourTurn) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = (e.clientX - rect.left) * (CANVAS_W / rect.width);
    const my = (e.clientY - rect.top) * (CANVAS_H / rect.height);
    const tank = g.tanks[g.turn];
    const dx = mx - tank.x;
    const dy = tank.y + tank.height - my;
    let angle = Math.atan2(dy, Math.abs(dx)) * (180 / Math.PI);
    angle = Math.max(5, Math.min(175, angle));
    const dir = g.turn === 0 ? 1 : -1;
    if (dir * dx < 0) angle = 180 - angle;
    setGame((prev) => {
      const tanks = prev.tanks.map((t) => ({ ...t }));
      tanks[prev.turn].angle = Math.max(5, Math.min(175, angle));
      return { ...prev, tanks: tanks as [typeof tanks[0], typeof tanks[0]] };
    });
  }

  function handleMouseDown() {
    const g = gameRef.current;
    if (g.inFlight || g.winner) return;
    if (mode === "multiplayer" && !yourTurn) return;
    chargeRef.current = g.tanks[g.turn].power;
    mouseDownRef.current = true;
  }

  function handleMouseUp() {
    if (!mouseDownRef.current) return;
    mouseDownRef.current = false;
    const g = gameRef.current;
    if (g.inFlight || g.winner) return;
    if (mode === "multiplayer") {
      apiPost("/api/tankwars/fire", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    setGame((prev) => fire(prev));
  }

  function handleMouseLeave() {
    if (mouseDownRef.current) handleMouseUp();
  }

  // Keyboard for movement (A/D)
  useEffect(() => {
    const keys = new Set<string>();
    function handleKey(e: KeyboardEvent) {
      keys.add(e.key);
      if (e.key === "a" || e.key === "A" || e.key === "d" || e.key === "D" || e.key === "ArrowLeft" || e.key === "ArrowRight") e.preventDefault();
    }
    function handleKeyUp(e: KeyboardEvent) { keys.delete(e.key); }
    const interval = setInterval(() => {
      setGame((prev) => {
        if (prev.inFlight || prev.winner) return prev;
        let g = prev;
        if (keys.has("ArrowLeft") || keys.has("a") || keys.has("A")) g = moveTank(g, -1);
        if (keys.has("ArrowRight") || keys.has("d") || keys.has("D")) g = moveTank(g, 1);
        return g;
      });
    }, 16);
    window.addEventListener("keydown", handleKey);
    window.addEventListener("keyup", handleKeyUp);
    return () => { clearInterval(interval); window.removeEventListener("keydown", handleKey); window.removeEventListener("keyup", handleKeyUp); };
  }, []);

  // ---- multiplayer --------------------------------------------------------

  async function apiPost(path: string, body: object) {
    try { const res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }); return await res.json(); }
    catch { return { type: "error", message: "Network error." }; }
  }

  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined":
        setOpponentName(event.playerName); setMpConnected(true);
        setMessage(`Opponent ${event.playerName} joined! Press New Game to start.`); break;
      case "game-state":
        prevGameRef.current = gameRef.current;
        setGame(event.game);
        setYourTurn(event.yourTurn);
        if (event.shot) {
          animStartRef.current = performance.now();
          setAnimShot(event.shot);
        }
        break;
      case "opponent-left":
        setMpConnected(false); setRoomCode(""); setOpponentName(""); setMpPlayerId(null); setMpToken("");
        setMessage("Opponent left."); setGame(createGame()); break;
      case "error": setMpError(event.message); break;
    }
  }

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    async function poll() {
      if (!pollingRef.current) return;
      const code = roomCodeRef.current, pid = mpPlayerIdRef.current;
      if (!code || !pid) { pollingRef.current = false; return; }
      try {
        const res = await fetch(`/api/poll?code=${encodeURIComponent(code)}&playerId=${pid}`);
        const data = await res.json();
        for (const event of data.events) { handleServerEvent(event); if (event.type === "opponent-left") { pollingRef.current = false; return; } }
      } catch { /* retry */ }
      if (pollingRef.current) pollRef.current = setTimeout(poll, 200);
    }
    poll();
  }, []);

  function stopPolling() { pollingRef.current = false; if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } }
  useEffect(() => { return () => stopPolling(); }, []);

  const [message, setMessage] = useState("");

  async function handleCreateRoom() {
    const result = await apiPost("/api/create-room", { playerName: p1Name.trim() || "Player 1", playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = result.code; mpPlayerIdRef.current = result.playerId;
    setRoomCode(result.code); setMpPlayerId(result.playerId); setMpToken(result.token);
    setMessage(`Room created! Share: ${result.code}`); startPolling();
  }

  async function handleJoinRoom() {
    if (!joinCode.trim()) { setMpError("Enter a room code."); return; }
    const result = await apiPost("/api/join-room", { code: joinCode.trim(), playerName: p1Name.trim() || "Player 2", playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = joinCode.trim(); mpPlayerIdRef.current = result.playerId;
    setMpPlayerId(result.playerId); setMpToken(result.token); setRoomCode(joinCode.trim());
    setOpponentName(result.opponentName); setP1Color(result.opponentColor);
    setMpConnected(true); setMessage(`Connected with ${result.opponentName}. Press New Game to start!`);
    startPolling();
  }

  async function handleLeaveGame() {
    await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
    stopPolling(); setMpConnected(false); setRoomCode(""); setOpponentName(""); setMpPlayerId(null); setMpToken("");
    setGame(createGame()); setMessage("Left.");
  }

  function handleModeChange(next: Mode) {
    if (mode === "multiplayer") { if (roomCode && mpToken) apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken }); stopPolling(); }
    setMode(next); setGame(createGame()); chargeRef.current = 0; setMessage("");
  }

  return (
    <div className="game-shell">
      <ConfettiSplash active={game.winner !== null} winnerName={game.winner !== null ? `Player ${game.winner}` : undefined} />
      {!hideTitle && <h1 className="title">Tank Wars</h1>}
      <section className="control-panel">
        <p className="status">{game.message}</p>
        {game.inFlight && <p className="subtle">Projectile in flight...</p>}
        <div className="row two-col">
          <label>Mode <select value={mode} onChange={(e) => handleModeChange(e.target.value as Mode)}>
            <option value="cpu">vs Computer</option>
            <option value="local">Two Players Local</option>
            <option value="multiplayer">Multiplayer Online</option>
          </select></label>
        </div>

        {mode === "multiplayer" && !mpConnected && (
          <div className="row two-col"><label>Your Name <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={20} /></label>
          <label>Your Color <select value={p1Color} onChange={(e) => setP1Color(e.target.value as PlayerColor)}>{COLOR_OPTIONS.map((c) => (<option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>))}</select></label></div>
        )}
        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="row two-col"><label>Room Code <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} maxLength={40} /></label></div>
        )}
        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="mp-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="mp-btn" onClick={handleCreateRoom}>Create Room</button>
            <button className="mp-btn mp-btn-secondary" onClick={handleJoinRoom}>Join Room</button>
          </div>
        )}
        {mode === "multiplayer" && roomCode && !mpConnected && (
          <div style={{ textAlign: 'center' }}>
            <p className="mp-label">Share this code with a friend:</p><p className="mp-code">{roomCode}</p>
            <p className="mp-waiting">Waiting for opponent...</p>
            <button className="mp-cancel" onClick={handleLeaveGame}>Cancel</button>
          </div>
        )}
        {mode === "multiplayer" && mpConnected && (
          <div className="mp-connected-bar"><span className="mp-connected-info">Connected with <strong>{opponentName}</strong></span></div>
        )}

        <div className="actions">
          {(mode !== "multiplayer" || mpConnected) && <button onClick={resetGame}>New Game</button>}
          {mode === "multiplayer" && mpConnected && <button className="mp-leave" onClick={handleLeaveGame}>Leave Game</button>}
        </div>
        {mpError && <p className="subtle mp-error">{mpError}</p>}
      </section>

      <section className="board-panel">
        <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} className="tankwars-canvas"
          onMouseMove={handleMouseMove} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} onMouseLeave={handleMouseLeave} />
        <div className="how-to-play">
          <p className="how-title">How to Play</p>
          <p>1. A/D or ← → to move. Move mouse to aim.</p>
          <p>2. Hold click to charge power. Release to fire!</p>
          <p>3. Reduce enemy HP to 0 to win!</p>
        </div>
      </section>
    </div>
  );
}
